// File: webelieve-frontend/src/features/notes/components/NotePrivilegeWarning.tsx
// Interstitial warning when sharing privileged notes

import React from 'react';

interface NotePrivilegeWarningProps {
  isOpen: boolean;
  confidentiality: string;
  noteTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const CONFIDENTIALITY_LABELS: Record<string, string> = {
  'attorney-work-product': 'Attorney Work Product',
  'attorney-client-privilege': 'Attorney-Client Privilege',
};

const NotePrivilegeWarning: React.FC<NotePrivilegeWarningProps> = ({
  isOpen,
  confidentiality,
  noteTitle,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const label = CONFIDENTIALITY_LABELS[confidentiality] || confidentiality;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Privileged Note Warning
            </h3>
            <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
              {label}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-3">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            The note <span className="font-medium text-gray-900 dark:text-white">"{noteTitle}"</span> is
            classified as <span className="font-medium text-amber-700 dark:text-amber-300">{label}</span>.
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-200 space-y-1.5">
            <p className="font-medium">Sharing this note may:</p>
            <ul className="list-disc list-inside space-y-0.5 text-amber-700 dark:text-amber-300">
              <li>Waive attorney-client privilege protections</li>
              <li>Expose work product to opposing counsel risk</li>
              <li>Create discoverable communication records</li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
          >
            I Understand, Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotePrivilegeWarning;
